import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OrderApiService } from '~services/api/order.api.service';
import { ErrorHandlingService } from '~services/error-handling.service';
import { UserService } from '~services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, Observable } from 'rxjs';
import { TruckingVendor } from '~lmo/models/trucking-vendor.model';
import { LmoTruckingVendorsService } from '~lmo/services';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { fuse } from '~utilities/fuse';

export interface FormValue {
  startDate: Date;
  endDate: Date;
  email: boolean;
  byApprovedTime: boolean;
  vendors: TruckingVendor[];
}

const searchOptions: Fuse.FuseOptions<TruckingVendor> = {
  distance: 100,
  keys: ['name'],
  location: 0,
  maxPatternLength: 16,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

@Component({
  selector: 'sa-bulk-export',
  templateUrl: './bulk-export.component.html',
  styleUrls: ['./bulk-export.component.scss'],
})
export class BulkExportComponent implements OnInit {
  public downloadDataForm: FormGroup;
  public todayDate;
  public isDownloading = false;
  public allVendors$: Observable<TruckingVendor[]>;
  public vendorFilter = new FormControl();

  public get formValue(): FormValue {
    return this.downloadDataForm.value as FormValue;
  }

  constructor(
    private fb: FormBuilder,
    private orderApiService: OrderApiService,
    private errorHandler: ErrorHandlingService,
    private userService: UserService,
    private lmoTruckingVendorService: LmoTruckingVendorsService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.todayDate = new Date();
    this.setupForm();
  }

  private setupForm() {
    this.allVendors$ = combineLatest([
      this.lmoTruckingVendorService.vendorsWithContract$.pipe(map(fuse<TruckingVendor>(searchOptions))),
      this.vendorFilter.valueChanges.pipe(debounceTime(100), startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!searchTerm || searchTerm === '') {
          return fused.data;
        }
        return fused.fuse.search(searchTerm) as any;
      }),
    );

    this.downloadDataForm = this.fb.group({
      startDate: [this.todayDate, [Validators.required]],
      endDate: [this.todayDate, [Validators.required]],
      email: [false],
      byApprovedTime: [false, [Validators.required]],
      vendors: [null],
    });
  }

  public downloadData() {
    const value = this.formValue;
    const startTime = value.startDate.setHours(0, 0, 0);
    const endTime = value.endDate.setHours(23, 59, 59);
    if (!value.email) {
      this.isDownloading = true;
    }
    const selectedVendorIds = value?.vendors?.map((vendor) => vendor.id) ?? [];
    const body = {
      startTime: new Date(startTime),
      endTime: new Date(endTime),
      email: value.email,
      byApprovedTime: this.downloadDataForm.get('byApprovedTime').value,
      vendorIds: selectedVendorIds,
    };

    const endpoint = this.userService.isLMOAccount()
      ? this.orderApiService.downloadAllLmoData(body)
      : this.orderApiService.downloadAllVendorData(body);
    endpoint.subscribe(
      (data) => {
        if (!value.email) {
          window.open(data, '_blank');
          this.isDownloading = false;
          this.snackBar.open('successfully downloaded the data', null, {
            duration: 5000,
          });
        } else {
          this.snackBar.open('your bulk export will be emailed to you shortly', null, {
            duration: 5000,
          });
        }
      },
      (err) => {
        this.isDownloading = false;
        this.errorHandler.showError(err);
      },
    );
  }
}
